import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { EnrolledProgram } from "../lib/setupApi";

export type SelectableProgram = { program_id?: string; [key: string]: unknown };

const STORAGE_KEY = "SELF_LEARN_SELECTED_PROGRAM";

function readStoredProgramId(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { program_id?: string } | null;
    return parsed?.program_id ?? null;
  } catch {
    return null;
  }
}

function writeStoredProgram(program: SelectableProgram | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (program && program.program_id) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(program));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (quota/private mode) — non-fatal
  }
}

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => readStoredProgramId();

const updateSelection = (program: SelectableProgram | null) => {
  writeStoredProgram(program);
  listeners.forEach((l) => l());
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    listeners.forEach((l) => l());
  });
}

function applySelection(
  programs: EnrolledProgram[] | null,
  currentId: string | null,
): EnrolledProgram[] | null {
  if (!programs || programs.length === 0) return programs;

  const hasSelectedId = programs.some((p) => p.program_id === currentId);
  const fallbackId =
    programs.find((p) => p.isDefault)?.program_id ??
    programs[0]?.program_id ??
    null;
  const effectiveSelectedId = hasSelectedId ? currentId : fallbackId;

  return programs.map((p) => ({
    ...p,
    isDefault: p.program_id === effectiveSelectedId,
  }));
}

export function useSelectedProgram(enrolledPrograms: EnrolledProgram[] | null) {
  const currentId = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const normalizedPrograms = useMemo(
    () => applySelection(enrolledPrograms, currentId),
    [enrolledPrograms, currentId],
  );

  const selectedProgram = useMemo(() => {
    if (!normalizedPrograms || normalizedPrograms.length === 0) return null;
    return normalizedPrograms.find((p) => p.isDefault) ?? normalizedPrograms[0];
  }, [normalizedPrograms]);

  const setSelectedProgram = useCallback(
    (program: SelectableProgram | null) => {
      updateSelection(program);
    },
    [],
  );

  return {
    normalizedPrograms,
    selectedProgram,
    setSelectedProgram,
  };
}
